import { useQuery } from "@tanstack/react-query";
import { isEmpty, snakeCase, uniqBy } from "lodash/fp";
import { DateTime } from "luxon";

import {
  getDimensionMetaById,
  MultiDimensionMeta,
  QueryDimension,
  SingleDimensionMeta,
} from "@app/analytics/meetops/dimensions";
import {
  ComputedMetricMeta,
  getMetricMetaById,
  MetricMeta,
  QueryMetric,
  SimpleMetricMeta,
} from "@app/analytics/meetops/metrics";
import {
  Metric,
  MeetOpsQuery,
  MeetOpsQueryBody,
  SessionMeetOpsQuery,
  SessionMeetOpsQueryBody,
  Filter,
} from "@app/analytics/meetops/types";
import { getBaseQuery } from "@app/analytics/meetops/utils";
import { post } from "@app/common/api";
import useAuth from "@app/common/auth/useAuth";
import { withQueryParamsUrl } from "@app/common/utils";
import Config from "@app/config";

export interface QueryResult {
  data: any[];
}

export interface MeetOpsQueryResult {
  results: QueryResult[];
}

const getDateString = (date: DateTime | string): string =>
  typeof date === "string" ? date : date.toISO() ?? "N/A";

const getDateRangeStrings = (
  ranges: { startDate: string | DateTime; endDate: string | DateTime }[]
): { startDate: string; endDate: string }[] => {
  return ranges.map((range) => ({
    ...range,
    startDate: getDateString(range.startDate),
    endDate: getDateString(range.endDate),
  }));
};

const getQueryKey = (query: MeetOpsQuery | null): any[] => {
  return ["metricsQuery", query];
};

const getMetricMeta = (metricMeta: QueryMetric | MetricMeta) =>
  typeof metricMeta === "string" ? getMetricMetaById(metricMeta) : metricMeta;

const toQueryMetric = (metricMeta: SimpleMetricMeta): Metric => ({
  name: metricMeta.queryName,
  alias: metricMeta.id,
  filters: metricMeta.getFilters(),
});

const toQueryDimensions = (
  dimension: QueryDimension | SingleDimensionMeta | MultiDimensionMeta
): { name: string }[] => {
  let dimensionMeta;
  if (typeof dimension === "string") {
    dimensionMeta = getDimensionMetaById(dimension);
  } else {
    dimensionMeta = dimension;
  }

  if ("getDimensions" in dimensionMeta) {
    return dimensionMeta.getDimensions().map(toQueryDimensions).flat();
  }
  return [toQueryDimension(dimensionMeta)];
};

const toQueryDimension = (dimension: SingleDimensionMeta) => {
  return { name: snakeCase(dimension.queryName) };
};

const getMeetOpsQueryBody = (
  query: MeetOpsQuery | SessionMeetOpsQuery
): MeetOpsQueryBody | SessionMeetOpsQueryBody => {
  const queryMetricsMap: Map<string, Metric> = new Map();

  // Constructs a unique set of query metrics from the query
  // this code will expand computed metrics into their query metric
  // components
  query.metrics.forEach((metric) => {
    const metricMeta = getMetricMeta(metric);

    if (metricMeta.computed) {
      metricMeta
        .getMetrics()
        .forEach((metricMeta) =>
          queryMetricsMap.set(metricMeta.id, toQueryMetric(metricMeta))
        );
    } else {
      queryMetricsMap.set(metricMeta.id, toQueryMetric(metricMeta));
    }
  });

  let queryDimensions: QueryDimension[] = [];
  let queryFilters: Filter[] = [];

  if (query.userId) {
    const baseQuery = getBaseQuery(query.people, query.userId);
    queryDimensions = baseQuery.queryDimensions;
    queryFilters = baseQuery.queryFilters;
  }

  const body: MeetOpsQueryBody = {
    dateRanges: getDateRangeStrings(query.dateRanges),
    dimensions: uniqBy(
      "name",
      [...query.dimensions, ...queryDimensions].flatMap(toQueryDimensions)
    ),
    filters: [...(query.filters || []), ...queryFilters],
    metrics: Array.from(queryMetricsMap.values()),
  };

  if ("sessionId" in query) {
    const sessionBody: SessionMeetOpsQueryBody = {
      ...body,
      sessionId: query.sessionId,
    };
    return sessionBody;
  }

  return body;
};

const parseMetricFromResult = (
  rawResult: any,
  metricMeta: MetricMeta | ComputedMetricMeta,
  metrics: any
) => {
  let rawValue;
  if (metricMeta.computed) {
    metricMeta
      .getMetrics()
      .map((innerMetricMeta) =>
        parseMetricFromResult(rawResult, innerMetricMeta, metrics)
      );
    rawValue = metricMeta.getComputedValue(metrics);
  } else {
    rawValue = rawResult[metricMeta.id];
  }
  const value = metricMeta.toValue(rawValue);
  const formattedValue = metricMeta.toFormat(value);
  const parsedMetric = { id: metricMeta.id, rawValue, value, formattedValue };
  metrics[metricMeta.id] = parsedMetric;
  return parsedMetric;
};

const parseDimensionsFromResult = (
  rawResult: any,
  dimensions: (QueryDimension | SingleDimensionMeta | MultiDimensionMeta)[]
) => {
  // Flatten the dimension list
  const allDimensions: SingleDimensionMeta[] = dimensions.flatMap((dim) => {
    const dimensionMeta = getDimensionMetaById(dim);
    return "getDimensions" in dimensionMeta
      ? dimensionMeta.getDimensions()
      : dimensionMeta;
  });

  // Compute results for all simple dimensions, so later
  // we can process computed dimensions
  const simpleResult = allDimensions.reduce<any>((acc, dim) => {
    acc[dim.id] = rawResult[dim.queryName];
    return acc;
  }, {});

  // Process computed dimensions
  return dimensions.reduce<any>((acc, dim) => {
    if (typeof dim !== "string" && "toValue" in dim) {
      acc[dim.id] = dim.toValue(acc);
    }
    return acc;
  }, simpleResult);
};

const getParsedResultMetrics = (
  query: Partial<MeetOpsQuery>,
  rawQueryResults: any
): MeetOpsQueryResult => {
  if (isEmpty(rawQueryResults)) return { results: [] };

  const results = rawQueryResults.map((rawQueryResult: any) => {
    const results = rawQueryResult.results.map((rawResult: any) => {
      const metrics: any = {};
      (query.metrics || []).forEach((metric) => {
        const metricMeta = getMetricMeta(metric);
        parseMetricFromResult(rawResult, metricMeta, metrics);
      });
      return {
        ...parseDimensionsFromResult(rawResult, query.dimensions || []),
        ...metrics,
      };
    });
    return { data: results };
  });
  return { results };
};

const queryParamValue = (value?: string | number) =>
  value ? encodeURIComponent(value) : null;

export const queryMetricsForResult = async (
  query: MeetOpsQuery | SessionMeetOpsQuery | null,
  isForCurrentUser: boolean
): Promise<MeetOpsQueryResult> => {
  if (query == null) {
    throw "query must not be null";
  }

  const rootPath =
    "sessionId" in query
      ? "/metrics/meeting_ops/session"
      : "/metrics/meeting_ops";

  // TODO: pass these in the request body instead of query params once supported by BE
  const queryPath = withQueryParamsUrl(rootPath, {
    session_id: "sessionId" in query ? queryParamValue(query.sessionId) : null,
    workspace_id: queryParamValue(query.workspaceId),
    team_id: queryParamValue(query.teamId),
    user_id: isForCurrentUser ? queryParamValue(query.userId) : null,
  });

  const queryBody = getMeetOpsQueryBody(query);

  const rawQueryResults = await post(queryPath, queryBody, {
    withCreds: true,
  });

  return getParsedResultMetrics(query, rawQueryResults);
};

const useMetricsQuery = (
  query: MeetOpsQuery | SessionMeetOpsQuery | null,
  options?: {
    enabled?: boolean;
    queryKey?: any;
    staleTime?: number;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    includeWorkspaceId?: boolean;
    cacheTime?: number;
  }
) => {
  const { user } = useAuth();

  let meetOpsQuery: MeetOpsQuery | SessionMeetOpsQuery | null = query;
  if (user?.workspaceId && query && (options?.includeWorkspaceId ?? true)) {
    meetOpsQuery = { ...query, workspaceId: user.workspaceId };
  }

  const isForCurrentUser = query?.userId != null && query.userId == user?.id;

  return useQuery({
    queryKey: getQueryKey(options?.queryKey || meetOpsQuery),
    queryFn: () => queryMetricsForResult(meetOpsQuery, isForCurrentUser),
    ...options,
    enabled: !Config.api.disableMetricsQueries && (options?.enabled ?? true),
  });
};

export default useMetricsQuery;
